import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { exhaustiveSwitchWithDefault } from "@faire/web--source/common/exhaustiveSwitchWithDefault";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrl, optimizeImageUrlFillRectangle, optimizeImageUrlFillSquare, optimizeImageUrlHeight, optimizeImageUrlWidth, optimizeProductImageUrl, optimizeProductImageUrlFillVertical } from "@faire/web--source/common/images/optimizeImages";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import * as React from "react";
export var ImageTransformationType;
(function(ImageTransformationType) {
    ImageTransformationType["FillRectangle"] = "FillRectangle";
    ImageTransformationType["FillSquare"] = "FillSquare";
    ImageTransformationType["Width"] = "Width";
    ImageTransformationType["Height"] = "Height";
    ImageTransformationType["Product"] = "Product";
    ImageTransformationType["ProductFillVertical"] = "ProductFillVertical";
})(ImageTransformationType || (ImageTransformationType = {}));
function loadImage(imageUrl, onLoad) {
    try {
        var window = getWindowOrThrow();
        var img = new window.Image();
        img.onload = onLoad;
        img.src = imageUrl;
        img.onerror = onLoad;
    } catch (_) {
        onLoad();
    }
}
function convertToTinySize(transformation) {
    var type = transformation === null || transformation === void 0 ? void 0 : transformation.type;
    if (!type) {
        return undefined;
    }
    var SMALLER_IMAGE_SIZE = 60;
    switch(type){
        case ImageTransformationType.FillRectangle:
            return {
                type: type,
                width: SMALLER_IMAGE_SIZE,
                height: Math.round(SMALLER_IMAGE_SIZE * (transformation.height / transformation.width))
            };
        case ImageTransformationType.Height:
            return {
                type: type,
                height: SMALLER_IMAGE_SIZE
            };
        case ImageTransformationType.FillSquare:
        case ImageTransformationType.Width:
        case ImageTransformationType.Product:
        case ImageTransformationType.ProductFillVertical:
            return {
                type: type,
                width: SMALLER_IMAGE_SIZE
            };
        default:
            return exhaustiveSwitchWithDefault(undefined)(type);
    }
}
function doTransformation(image, isSSR, options, transformation) {
    var type = transformation === null || transformation === void 0 ? void 0 : transformation.type;
    if (!type) {
        return optimizeImageUrl(image, isSSR, options);
    }
    switch(type){
        case ImageTransformationType.FillRectangle:
            return optimizeImageUrlFillRectangle(image, isSSR, transformation.width, transformation.height, options);
        case ImageTransformationType.FillSquare:
            return optimizeImageUrlFillSquare(image, isSSR, transformation.width, options);
        case ImageTransformationType.Width:
            return optimizeImageUrlWidth(image, isSSR, transformation.width, options);
        case ImageTransformationType.Height:
            return optimizeImageUrlHeight(image, isSSR, transformation.height, options);
        case ImageTransformationType.Product:
            return optimizeProductImageUrl(image, isSSR, transformation.width, options);
        case ImageTransformationType.ProductFillVertical:
            return optimizeProductImageUrlFillVertical(image, isSSR, transformation.width, options);
        default:
            return exhaustiveSwitchWithDefault(undefined)(type);
    }
}
export var ProgressiveProductImage = function(param) {
    var image = param.image, options = param.options, transformation = param.transformation, isSSR = param.isSSR, alt = param.alt, loading = param.loading, className = param.className, onLoad = param.onLoad;
    var _React_useState = _sliced_to_array(React.useState(function() {
        return getInitialImageUrl(image, isSSR, options, transformation);
    }), 2), imageUrl = _React_useState[0], setImageUrl = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(true), 2), isInitial = _React_useState1[0], setIsInitial = _React_useState1[1];
    React.useEffect(function() {
        var fullSizeUrl = doTransformation(image, isSSR, options, transformation);
        if (fullSizeUrl) {
            loadImage(fullSizeUrl, function() {
                setImageUrl(fullSizeUrl);
                setIsInitial(false);
            });
        } else {
            setIsInitial(false);
        }
    }, [
        image,
        isSSR,
        transformation,
        options
    ]);
    return /*#__PURE__*/ _jsx("img", {
        "data-faire-image-id": image.token,
        loading: loading,
        src: imageUrl,
        alt: alt,
        className: className,
        "data-product-image": isInitial ? "initial" : "full",
        onLoad: onLoad
    });
};
function getInitialImageUrl(image, isSSR, options, transformation) {
    if (isSSR) {
        /**
         * Use the full-size image for direct loads with SSR.
         * Our client JS bundles are large, and it can take a long time before JS executes.
         * Since we can't render both tiny and full-size images with SSR, let's use the
         * full-size to avoid delaying the start of that request.
         */ return doTransformation(image, isSSR, _object_spread_props(_object_spread({}, options), {
            // With SSR we don't know the client's DPR, so assume 1.
            dprOverride: 1
        }), transformation);
    } else {
        return doTransformation(image, isSSR, _object_spread_props(_object_spread({}, options), {
            // It doesn't make sense to have a 2x or 3x tiny image, so use DPR of 1.
            dprOverride: 1
        }), convertToTinySize(transformation));
    }
}
var DEFAULT_IMAGE_WIDTH = 720;
var NORMALIZED_IMAGE_WIDTHS = [
    360,
    720,
    1080,
    1440,
    1800
];
/**
 * Gets an image and generates a normalized (multiples of 360) srcset for it.
 */ export function getNormalizedImageUrls(image, showVerticalImagery, options) {
    // we override DPR, isSSR value won't be needed:
    var urls = NORMALIZED_IMAGE_WIDTHS.map(function(width) {
        var url = doTransformation(image, false, _object_spread_props(_object_spread({}, options), {
            width: width,
            dprOverride: 1
        }), {
            type: showVerticalImagery ? ImageTransformationType.ProductFillVertical : ImageTransformationType.Product,
            width: width
        });
        if (url) {
            return "".concat(url, " ").concat(width, "w");
        }
        return url;
    }).filter(isNotUndefined);
    return urls.join(", ");
}
export function getDefaultImageUrl(image, showVerticalImagery, options) {
    // we override DPR, isSSR value won't be needed:
    var url = doTransformation(image, false, _object_spread_props(_object_spread({}, options), {
        width: DEFAULT_IMAGE_WIDTH,
        dprOverride: 1
    }), {
        type: showVerticalImagery ? ImageTransformationType.ProductFillVertical : ImageTransformationType.Product,
        width: DEFAULT_IMAGE_WIDTH
    });
    if (url) {
        return url;
    }
    var _image_url;
    // this should not happen (safe default):
    return (_image_url = image.url) !== null && _image_url !== void 0 ? _image_url : "";
}
